import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { AuthProvider } from "@/contexts/AuthContext";
import { NotificationProvider } from "@/contexts/NotificationContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import SubdomainRouter from "@/components/SubdomainRouter";
import LandingPage from "@/pages/LandingPage";
import QuotePage from "@/pages/QuotePage";
import SuccessPage from "@/pages/SuccessPage";
import PolicyholderInfoPage from "@/pages/PolicyholderInfoPage";
import PolicyConfirmationPage from "@/pages/PolicyConfirmationPage";
import DocumentCenterPage from "@/pages/DocumentCenterPage";
import CancellationPage from "@/pages/CancellationPage";
import PortalLoginPage from "@/pages/PortalLoginPage";
import PortalDashboardPage from "@/pages/PortalDashboardPage";
import PortalCallbackPage from "@/pages/PortalCallbackPage";
import PasswordResetPage from "@/pages/PasswordResetPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import AccountSetupPage from "@/pages/AccountSetupPage";
import SettingsPage from "@/pages/SettingsPage";
import VerifyEmailChangePage from "@/pages/VerifyEmailChangePage";
import CoveragePage from "@/pages/CoveragePage";
import ClaimsListPage from "@/pages/ClaimsListPage";
import FileClaimPage from "@/pages/FileClaimPage";
import ClaimDetailPage from "@/pages/ClaimDetailPage";
import VehicleReassessmentPage from "@/pages/VehicleReassessmentPage";
import HelpCenterPage from "@/pages/HelpCenterPage";
import ContactSupportPage from "@/pages/ContactSupportPage";
import PublicSupportPage from "@/pages/PublicSupportPage";
import ComplaintNoticePage from "@/pages/ComplaintNoticePage";
import ScrollToTop from "@/components/ScrollToTop";
import PageTransition from "@/components/PageTransition";
import UnsubscribePage from "@/pages/UnsubscribePage";
import EmailPreferencesPage from "@/pages/EmailPreferencesPage";

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

function App() {
  const content = (
    <div className="App">
      <AuthProvider>
        <NotificationProvider>
          <BrowserRouter>
          <ScrollToTop />
          <SubdomainRouter>
            <PageTransition>
            <Routes>
              {/* ============================================ */}
              {/* SECURE SUBDOMAIN ROUTES (secure.losspay.com) */}
              {/* ============================================ */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/quote" element={<QuotePage />} />
              <Route path="/support" element={<PublicSupportPage />} />
              <Route path="/unsubscribe" element={<UnsubscribePage />} />
              <Route path="/email-preferences" element={<EmailPreferencesPage />} />
              <Route path="/success" element={<SuccessPage />} />
              <Route path="/payment-success" element={<SuccessPage />} />
              <Route path="/policyholder-info" element={<PolicyholderInfoPage />} />
              <Route path="/confirmation" element={<PolicyConfirmationPage />} />
              
              {/* ============================================ */}
              {/* SECURE SUBDOMAIN ROUTES (secure.losspay.com) */}
              {/* ============================================ */}
              
              {/* Auth routes - public */}
              <Route path="/signin" element={<PortalLoginPage />} />
              <Route path="/portal/login" element={<PortalLoginPage />} />
              <Route path="/portal/callback" element={<PortalCallbackPage />} />
              <Route path="/portal/reset-password" element={<PasswordResetPage />} />
              <Route path="/portal/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/portal/account-setup" element={<AccountSetupPage />} />
              <Route path="/policyholder/setup" element={<AccountSetupPage />} />
              <Route path="/account-setup" element={<AccountSetupPage />} />
              
              {/* Protected routes - require authentication */}
              <Route 
                path="/portal" 
                element={
                  <ProtectedRoute>
                    <PortalDashboardPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/documents/:policyNumber" 
                element={
                  <ProtectedRoute requirePolicyAccess={true}>
                    <DocumentCenterPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/cancel/:policyNumber" 
                element={
                  <ProtectedRoute requirePolicyAccess={true}>
                    <CancellationPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/settings" 
                element={
                  <ProtectedRoute>
                    <SettingsPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/coverage" 
                element={
                  <ProtectedRoute>
                    <CoveragePage />
                  </ProtectedRoute>
                } 
              />
              
              {/* Claims routes - require authentication */}
              <Route 
                path="/claims" 
                element={
                  <ProtectedRoute>
                    <ClaimsListPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/claims/file" 
                element={
                  <ProtectedRoute>
                    <FileClaimPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/claims/:claimNumber" 
                element={
                  <ProtectedRoute>
                    <ClaimDetailPage />
                  </ProtectedRoute>
                } 
              />
              
              {/* Vehicle reassessment - requires authentication */}
              <Route 
                path="/vehicle/reassessment" 
                element={
                  <ProtectedRoute>
                    <VehicleReassessmentPage />
                  </ProtectedRoute>
                } 
              />
              
              {/* Help Center - requires authentication */}
              <Route 
                path="/help" 
                element={
                  <ProtectedRoute>
                    <HelpCenterPage />
                  </ProtectedRoute>
                } 
              />
              
              {/* Contact Support - requires authentication */}
              <Route 
                path="/contact" 
                element={
                  <ProtectedRoute>
                    <ContactSupportPage />
                  </ProtectedRoute>
                } 
              />
              
              {/* Complaint Notice - requires authentication */}
              <Route 
                path="/complaint-notice" 
                element={
                  <ProtectedRoute>
                    <ComplaintNoticePage />
                  </ProtectedRoute>
                } 
              />
              
              {/* Email verification - public route */}
              <Route path="/verify-email-change" element={<VerifyEmailChangePage />} />
            </Routes>
            </PageTransition>
          </SubdomainRouter>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
        </NotificationProvider>
      </AuthProvider>
    </div>
  );

  // Only wrap with reCAPTCHA provider if site key is configured
  if (RECAPTCHA_SITE_KEY) {
    return (
      <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
        {content}
      </GoogleReCaptchaProvider>
    );
  }

  return content;
}

export default App;
